/**
 * A agenda de médicos da família, em tabela.
 *
 * Papel: mostra uma linha por médico, com o nome, a especialidade, quem ele
 * atende, o telefone, o e-mail, a clínica ou o endereço e a observação.
 *
 * Como usar: quem monta a tabela passa `onEditar` e `onRemover` apenas para
 * quem tem nível `editar`; sem eles a coluna de ações some inteira.
 *
 * `paciente` é texto livre: um médico costuma atender mais de uma pessoa da
 * família, ou todas, e a resposta não cabe no nome de uma pessoa só.
 *
 * Cada cabeçalho, menos o das ações, ordena a tabela pela regra
 * `'com-retorno'` de `useOrdenacao`: crescente, decrescente e de volta à ordem
 * em que `medicos` chegou, com o vazio sempre no fim. Todas as colunas ordenam
 * como texto, e o telefone pelo texto formatado que a célula mostra.
 */
import { type ContatoMedico } from '@concierge/shared';

import {
  CabecalhoOrdenavel,
  type ColunaOrdenavel,
  SETA_EM_TEXTO,
  useOrdenacao,
} from '@/shared/embutido';
import { formatarTelefone } from '@/shared/lib/formato';
import { Button } from '@/shared/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/shared/ui/table';

interface TabelaMedicosProps {
  medicos: ContatoMedico[];
  /** Chamado com o médico da linha; ausente esconde a coluna de ações. */
  onEditar?: (medico: ContatoMedico) => void;
  /** Chamado com o médico a excluir; a confirmação é de quem monta. */
  onRemover?: (medico: ContatoMedico) => void;
}

/** As colunas ordenáveis, na ordem e com o rótulo do cabeçalho. */
const COLUNAS: Record<string, ColunaOrdenavel<ContatoMedico> & { rotulo: string }> = {
  nome: { rotulo: 'Nome', tipo: 'texto', valor: (m) => m.nome },
  especialidade: { rotulo: 'Especialidade', tipo: 'texto', valor: (m) => m.especialidade },
  paciente: { rotulo: 'Paciente(s)', tipo: 'texto', valor: (m) => m.paciente },
  telefone: {
    rotulo: 'Telefone',
    tipo: 'texto',
    valor: (m) => (m.telefone === null ? null : formatarTelefone(m.telefone)),
  },
  email: { rotulo: 'E-mail', tipo: 'texto', valor: (m) => m.email },
  endereco: { rotulo: 'Clínica/Endereço', tipo: 'texto', valor: (m) => m.endereco },
  observacao: { rotulo: 'Obs.', tipo: 'texto', valor: (m) => m.observacao },
};

/**
 * A tabela de médicos.
 *
 * @param medicos - Os médicos a mostrar, na ordem que vale antes de qualquer
 *   clique e à qual o terceiro clique devolve.
 * @param onEditar - Chamado ao clicar em editar; ausente esconde as ações.
 * @param onRemover - Chamado ao clicar em remover; ausente esconde as ações.
 * @returns A tabela com uma linha por médico.
 */
export function TabelaMedicos({ medicos, onEditar, onRemover }: TabelaMedicosProps) {
  const mostrarAcoes = onEditar !== undefined || onRemover !== undefined;
  const ordenacao = useOrdenacao(medicos, COLUNAS, 'com-retorno');

  return (
    <Table>
      <TableHeader>
        <TableRow>
          {Object.entries(COLUNAS).map(([chave, coluna]) => (
            <CabecalhoOrdenavel
              key={chave}
              como={TableHead}
              title={`Ordenar por ${coluna.rotulo}`}
              seta={ordenacao.seta(chave)}
              classeDaSeta={SETA_EM_TEXTO}
              onAlternar={() => ordenacao.alternar(chave)}
            >
              {coluna.rotulo}
            </CabecalhoOrdenavel>
          ))}
          {mostrarAcoes && (
            <TableHead>
              <span className="sr-only">Ações</span>
            </TableHead>
          )}
        </TableRow>
      </TableHeader>
      <TableBody>
        {ordenacao.linhas.map((medico) => (
          <TableRow key={medico.id}>
            <TableCell className="text-texto-medio">{medico.nome}</TableCell>
            <TableCell>{medico.especialidade ?? '—'}</TableCell>
            <TableCell>{medico.paciente ?? '—'}</TableCell>
            <TableCell>
              {medico.telefone === null ? '—' : formatarTelefone(medico.telefone)}
            </TableCell>
            <TableCell>{medico.email ?? '—'}</TableCell>
            <TableCell className="text-[12.5px] text-texto-fraco">
              {medico.endereco ?? '—'}
            </TableCell>
            <TableCell className="text-[12.5px] text-texto-fraco">
              {medico.observacao ?? '—'}
            </TableCell>
            {mostrarAcoes && (
              <TableCell>
                <div className="flex gap-2">
                  {onEditar !== undefined && (
                    <Button variant="outline" size="sm" onClick={() => onEditar(medico)}>
                      editar
                    </Button>
                  )}
                  {onRemover !== undefined && (
                    <Button variant="destructive" onClick={() => onRemover(medico)}>
                      remover
                    </Button>
                  )}
                </div>
              </TableCell>
            )}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
